import {
  Combobox,
  type ComboboxRootChangeEventDetails,
} from '@base-ui/react/combobox'
import type { BaseUIEvent } from '@base-ui/react/types'
import clsx from 'clsx'
import {
  type KeyboardEvent,
  type Ref,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'

import { Icon } from '@fv/client-components'
import { submitForm, validateEmailList } from '@fv/client-core'

export type ValidateAndAddResult = {
  value?: string[]
  inputValue: string
  success: boolean
}

export type ContactListInputHandle = {
  validateAndAddValue: (report: boolean) => ValidateAndAddResult
}

export type ContactListInputClassNames = {
  root?: string
  chipsContainer?: string
  chip?: string
  chipLabel?: string
  chipRemove?: string
  inputWrapper?: string
  input?: string
  positioner?: string
  popup?: string
  list?: string
  item?: string
  empty?: string
}

export type ContactListInputProps = {
  value: string[]
  onChange: (val: string[]) => void
  options?: string[]
  autoFocus?: boolean
  required?: boolean
  hideValidationOutline?: boolean
  classNames?: ContactListInputClassNames
  placeholder?: string
  portalToRoot?: boolean
  ref?: Ref<ContactListInputHandle>
}

const separatorRegex = /[\s,;]/g
const cleanEmail = (email: string) => email.split(separatorRegex)

const dedupeEmails = (emails: string[]): string[] => {
  const seen = new Set<string>()
  return emails.filter(e => {
    const lower = e.toLowerCase()
    if (seen.has(lower)) return false
    seen.add(lower)
    return true
  })
}

export const ContactListInput = ({
  value,
  onChange,
  options,
  autoFocus,
  required,
  hideValidationOutline,
  classNames,
  ref,
  placeholder = 'e.g. bill@example.com, ted@example.com',
  portalToRoot = false,
}: ContactListInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const chipsRef = useRef<HTMLDivElement>(null)
  const [inputValue, setInputValue] = useState('')

  const hasOptions = !!options?.length
  const items = useMemo(() => [...value, ...(options ?? [])], [value, options])

  const fireChange = (values: string[]) => {
    onChange(dedupeEmails(values).map(v => v.toLowerCase()))
  }

  const scrollToBottom = () => {
    setTimeout(() => {
      chipsRef.current?.scrollTo({
        top: chipsRef.current.scrollHeight,
        behavior: 'smooth',
      })
    }, 5)
  }

  const setValidity = (msg: string) => {
    inputRef.current?.setCustomValidity(msg)
  }

  const doSubmit = () => submitForm(inputRef.current?.closest('form'))

  const reportValidity = (validity: boolean) => {
    setValidity(validity ? '' : 'Please enter a valid email address')
    if (!validity) doSubmit()
  }

  const validateAndAddValue = (report = false): ValidateAndAddResult => {
    let newValue = value
    if (required && !newValue.length && !inputValue && report) {
      setValidity('At least one email address is required')
      doSubmit()
      return { inputValue, success: false, value: newValue }
    }
    if (!inputValue) return { inputValue, success: true, value: newValue }

    const success = validateEmailList(inputValue)
    const cleaned = cleanEmail(inputValue).filter(Boolean)
    if (success) {
      newValue = [...value, ...cleaned]
      fireChange(newValue)
      setInputValue('')
      scrollToBottom()
    }
    if (report) reportValidity(success)
    return {
      success,
      inputValue: cleaned.join(),
      value: newValue,
    }
  }

  const handleValueChange = (next: string[]) => {
    fireChange(next)
    if (next.length > value.length) {
      setInputValue('')
      setValidity('')
      scrollToBottom()
    }
  }

  const handleKeyDown = (
    event: BaseUIEvent<KeyboardEvent<HTMLInputElement>>,
  ) => {
    if (!inputValue) {
      if (event.key === 'Backspace') {
        event.preventBaseUIHandler()
      }
      return
    }
    switch (event.key) {
      case ',':
      case ';':
      case ' ':
      case 'Enter':
      case 'Tab': {
        const { success } = validateAndAddValue()
        reportValidity(success)
        if (success) event.preventDefault()
      }
    }
  }
  const handleInputValueChange = useCallback(
    (next: string, eventDetails: ComboboxRootChangeEventDetails) => {
      if (eventDetails.reason === 'input-clear') {
        eventDetails.cancel()
        return
      }
      setInputValue(next)
      inputRef.current?.setCustomValidity('')
    },
    [],
  )

  useImperativeHandle(ref, () => ({ validateAndAddValue }))

  const selectedKeys = useMemo(
    () => new Set(value.map(v => v.toLowerCase())),
    [value],
  )

  return (
    <Combobox.Root
      multiple
      items={items}
      value={value}
      onValueChange={handleValueChange}
      inputValue={inputValue}
      onInputValueChange={handleInputValueChange}
      filter={(item, query) =>
        (item as string).toLowerCase().includes(query.toLowerCase())
      }
      {...(!hasOptions && { open: false })}
    >
      <Combobox.Chips className={classNames?.root}>
        <Combobox.Value>
          {(chips: string[]) => (
            <>
              <div ref={chipsRef} className={classNames?.chipsContainer}>
                {chips.map(email => (
                  <Combobox.Chip
                    key={email}
                    className={classNames?.chip}
                    aria-label={email}
                  >
                    <span
                      className={classNames?.chipLabel}
                      onMouseDown={e => e.stopPropagation()} // stop base-ui from preventing text selection
                    >
                      {email}
                    </span>
                    <Combobox.ChipRemove className={classNames?.chipRemove}>
                      <Icon icon="trash" size="xs" />
                    </Combobox.ChipRemove>
                  </Combobox.Chip>
                ))}
              </div>
              <div className={classNames?.inputWrapper}>
                <Combobox.Input
                  ref={inputRef}
                  autoComplete="off"
                  className={clsx(
                    classNames?.input,
                    hideValidationOutline && 'hide-validation-outline',
                  )}
                  placeholder={chips.length === 0 ? placeholder : undefined}
                  autoFocus={autoFocus}
                  onKeyDown={handleKeyDown}
                />
              </div>
            </>
          )}
        </Combobox.Value>
      </Combobox.Chips>

      {hasOptions && (
        <Combobox.Portal
          container={portalToRoot ? document.getElementById('root') : undefined}
        >
          <Combobox.Positioner
            className={clsx(classNames?.positioner, 'z-[9999]')}
            sideOffset={4}
            style={{ width: 'var(--anchor-width)' }}
          >
            <Combobox.Popup className={classNames?.popup}>
              <Combobox.List className={classNames?.list}>
                {(item: string) => {
                  if (selectedKeys.has(item.toLowerCase())) return null
                  return (
                    <Combobox.Item
                      key={item}
                      value={item}
                      className={classNames?.item}
                    >
                      {item}
                    </Combobox.Item>
                  )
                }}
              </Combobox.List>
            </Combobox.Popup>
          </Combobox.Positioner>
        </Combobox.Portal>
      )}
    </Combobox.Root>
  )
}
